/**
 * Catálogo dos módulos do app, como eles são oferecidos na proposta.
 *
 * Existe por um defeito medido em produção: digitando o módulo a cada proposta, o MESMO serviço
 * virou três — "Canal de Denúncia", "Canal de Denúncias" e "Canal de Denúncias Pública" —, com
 * três descrições diferentes, enviadas a três clientes. Aqui o nome e o texto têm uma fonte só.
 *
 * Desativar em vez de excluir é o caminho normal: o módulo que saiu de linha some da escolha na
 * proposta e continua aqui, junto com a história de quem já o contratou.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { benefitModuleService } from '../../services/benefitModuleService';
import type { ModuloDoApp } from '../../types/benefitModule';

/** O rascunho do formulário. `id` ausente = módulo novo. */
interface Rascunho {
  id?: number;
  name: string;
  description: string;
  active: boolean;
  displayOrder: number;
}

const NOVO: Rascunho = { name: '', description: '', active: true, displayOrder: 0 };

export const BenefitModulesPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [modulos, setModulos] = useState<ModuloDoApp[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setModulos(await benefitModuleService.listar());
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : 'Não foi possível carregar os módulos');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const salvar = useCallback(async () => {
    if (!rascunho) return;
    setSalvando(true);
    try {
      const dados = {
        name: rascunho.name,
        description: rascunho.description.trim() === '' ? null : rascunho.description,
        active: rascunho.active,
        displayOrder: rascunho.displayOrder,
      };
      if (rascunho.id === undefined) {
        await benefitModuleService.criar(dados);
        enqueueSnackbar('Módulo cadastrado', { variant: 'success' });
      } else {
        await benefitModuleService.atualizar(rascunho.id, dados);
        enqueueSnackbar('Módulo salvo', { variant: 'success' });
      }
      setRascunho(null);
      await carregar();
    } catch (e: unknown) {
      // A recusa de nome repetido chega por aqui, e é ela que a pessoa precisa ler.
      enqueueSnackbar(e instanceof Error ? e.message : 'Não foi possível salvar', {
        variant: 'error',
      });
    } finally {
      setSalvando(false);
    }
  }, [rascunho, enqueueSnackbar, carregar]);

  const alternarAtivo = useCallback(
    async (m: ModuloDoApp) => {
      try {
        await benefitModuleService.atualizar(m.id, { active: !m.active });
        await carregar();
      } catch (e: unknown) {
        enqueueSnackbar(e instanceof Error ? e.message : 'Não foi possível alterar', {
          variant: 'error',
        });
      }
    },
    [carregar, enqueueSnackbar]
  );

  const excluir = useCallback(
    async (m: ModuloDoApp) => {
      try {
        await benefitModuleService.remover(m.id);
        enqueueSnackbar('Módulo excluído', { variant: 'success' });
        await carregar();
      } catch (e: unknown) {
        enqueueSnackbar(e instanceof Error ? e.message : 'Não foi possível excluir', {
          variant: 'error',
        });
      }
    },
    [carregar, enqueueSnackbar]
  );

  const colunas: DataTableColumn<ModuloDoApp>[] = [
    { id: 'name', label: 'Módulo', primary: true, render: (m) => m.name },
    { id: 'description', label: 'Descrição', render: (m) => m.description ?? '—' },
    { id: 'displayOrder', label: 'Ordem', align: 'right', render: (m) => String(m.displayOrder) },
    {
      id: 'active',
      label: 'Situação',
      render: (m) => (
        <Chip
          size="small"
          label={m.active ? 'Ativo' : 'Inativo'}
          color={m.active ? 'success' : 'default'}
          onClick={() => void alternarAtivo(m)}
        />
      ),
    },
  ];

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Módulos do App' }]}
      title="Módulos do App"
      subtitle="O que a proposta oferece — cadastre uma vez e escolha a cada proposta"
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRascunho({ ...NOVO })}>
          Novo Módulo
        </Button>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <DataTable
        columns={colunas}
        rows={modulos}
        getRowId={(m) => m.id}
        loading={carregando}
        emptyState={{
          title: 'Nenhum módulo cadastrado ainda',
          description:
            'Os módulos são o que a proposta oferece ao cliente. Cadastre-os aqui uma vez e escolha a cada proposta, em vez de digitar o nome e a descrição de novo — foi assim que o mesmo módulo acabou com três nomes diferentes.',
          action: {
            label: 'Cadastrar módulo',
            node: 'modulos-do-app',
            deniedHint: 'Peça a um administrador da plataforma para cadastrar os módulos.',
          },
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            tooltip: 'Editar',
            onClick: (m) =>
              setRascunho({
                id: m.id,
                name: m.name,
                description: m.description ?? '',
                active: m.active,
                displayOrder: m.displayOrder,
              }),
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            tooltip: 'Excluir',
            color: 'error',
            onClick: (m) => void excluir(m),
          },
        ]}
      />

      <Dialog open={rascunho !== null} onClose={() => setRascunho(null)} fullWidth maxWidth="sm">
        <DialogTitle>{rascunho?.id === undefined ? 'Novo módulo' : 'Editar módulo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              required
              fullWidth
              value={rascunho?.name ?? ''}
              onChange={(e) => setRascunho((r) => (r ? { ...r, name: e.target.value } : r))}
              helperText="É o nome que sai na proposta. Dois módulos não podem ter o mesmo."
            />
            <TextField
              label="Descrição"
              fullWidth
              multiline
              minRows={2}
              value={rascunho?.description ?? ''}
              onChange={(e) => setRascunho((r) => (r ? { ...r, description: e.target.value } : r))}
              helperText="O texto que explica o módulo ao cliente. Dá para ajustar na proposta sem mexer aqui."
            />
            <TextField
              label="Ordem"
              type="number"
              value={rascunho?.displayOrder ?? 0}
              onChange={(e) =>
                setRascunho((r) => (r ? { ...r, displayOrder: Number(e.target.value) } : r))
              }
              helperText="Menor aparece primeiro na escolha."
              sx={{ maxWidth: 200 }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={rascunho?.active ?? true}
                onChange={(e) => setRascunho((r) => (r ? { ...r, active: e.target.checked } : r))}
              />
              <span>Oferecido nas propostas</span>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRascunho(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void salvar()} disabled={salvando}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default BenefitModulesPage;
